import type { FC, ReactNode } from "react";
import { useCallback, createContext, useContext, useState } from "react";
import * as RadixToast from "@radix-ui/react-toast";
import { clsx } from "clsx";
import { Text } from "./Text";

export type ToastVariant = "success" | "warning" | "error" | "info" | "gray";

export type ToastProps = {
  timestamp?: number;
  title: ReactNode;
  description?: string;
  variant: ToastVariant;
  onClick?: () => void;
}

const DEFAULT_CLASSES = "rounded-3xl px-6 py-3";

const getClassNamesByVariant = (variant: ToastVariant) => {
  switch (variant) {
    case "success":
      return `bg-success-500 ${DEFAULT_CLASSES}`;
    case "warning":
      return `bg-warning-500 ${DEFAULT_CLASSES}`;
    case "error":
      return `bg-error-500 ${DEFAULT_CLASSES}`;
    case "info":
      return `bg-primary-100 ${DEFAULT_CLASSES}`;
    case "gray":
      return `bg-gray-800/60 ${DEFAULT_CLASSES}`;
  }
};

export const Toast: FC<ToastProps & { onRemoved?: () => void }> = ({
  title,
  description,
  variant,
  onClick,
  onRemoved,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <RadixToast.Root
      open={isOpen}
      onOpenChange={a => {
        if (!a && onRemoved) {
          setTimeout(() => {
            // Needed to wait until the close animation is done
            onRemoved();
          }, 150);
        }
      }}
      onClick={() => {
        if (onClick) {
          onClick();
        }
        setIsOpen(false);
      }}
      role="alert"
      className={clsx(
        `mb-3 w-fit max-w-sm cursor-pointer transition-all data-[state=closed]:animate-toasterSlideOut data-[state=open]:animate-toasterSlideIn`,
        getClassNamesByVariant(variant),
      )}>
      <RadixToast.Title>
        <Text color="text-white" fontWeight="font-bold" className="text-center">
          {title}
        </Text>
      </RadixToast.Title>
      {description ? (
        <RadixToast.Description>
          <Text color="text-white" className="mt-1 wrap-break-word text-center">
            {description}
          </Text>
        </RadixToast.Description>
      ) : null}
    </RadixToast.Root>
  );
};

const ToastViewport: FC<RadixToast.ToastViewportProps> = props => (
  <RadixToast.ToastViewport
    className="fixed right-1/2 top-4 z-100 flex translate-x-1/2 flex-col items-center"
    {...props}
  />
);

const Ctx = createContext<{
  addToast: (props: ToastProps) => void;
  removeToasts: () => void;
}>({
  addToast: () => {
    //
  },
  removeToasts: () => {
    //
  },
});

export const ToastProvider: FC<RadixToast.ToastProviderProps> = props => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const addToast = useCallback((toast: ToastProps) => {
    setToasts(old => [{ ...toast, timestamp: Date.now() }, ...old]);
  }, []);

  const removeToasts = useCallback(() => {
    setToasts(() => []);
  }, []);

  return (
    <Ctx value={{ addToast, removeToasts }}>
      <RadixToast.ToastProvider swipeDirection="up" duration={3000} {...props}>
        <ToastViewport />
        {toasts.map((t, index) => (
          <Toast
            key={t.timestamp ?? 0 * index}
            {...t}
            onRemoved={() => setToasts(o => o.filter(o => o.timestamp !== t.timestamp))}
          />
        ))}

        {props.children}
      </RadixToast.ToastProvider>
    </Ctx>
  );
};

export const useToasts = () => useContext(Ctx);
